const SCRIPT_SRC = 'https://js.hsforms.net/forms/embed/v2.js';
const SCRIPT_SELECTOR = 'script[data-hubspot-forms-script], script[src*="js.hsforms.net/forms/embed"]';
const READY_POLL_INTERVAL = 100;
const READY_TIMEOUT = 15000;
const DEFAULT_REGION = 'na1';
const DEFAULT_ERROR_MESSAGE = 'The form could not be loaded. Please try again later.';

let readyCallbacks = [];
let errorCallbacks = [];
let loading = false;
let targetCounter = 0;

const isHubSpotReady = () => typeof window.hbspt?.forms?.create === 'function';

const flush = (queue, argument) => {
    const callbacks = queue;
    readyCallbacks = [];
    errorCallbacks = [];
    loading = false;
    callbacks.forEach((callback) => callback(argument));
};

// The embed script sets window.hbspt asynchronously after its own load event, so polling is
// the only reliable signal that hbspt.forms.create is callable. Deadline-bounded, because a
// script tag can be present and still never define hbspt (blocked by an extension or CSP).
const pollForHubSpot = (deadline) => {
    if (isHubSpotReady()) {
        flush(readyCallbacks);
        return;
    }

    if (Date.now() >= deadline) {
        flush(errorCallbacks, new Error('HubSpot embed script never exposed hbspt.forms.create'));
        return;
    }

    window.setTimeout(() => pollForHubSpot(deadline), READY_POLL_INTERVAL);
};

const startPolling = () => pollForHubSpot(Date.now() + READY_TIMEOUT);

export const whenHubSpotReady = (onReady, onError) => {
    if (isHubSpotReady()) {
        onReady();
        return;
    }

    readyCallbacks.push(onReady);
    if (onError) {
        errorCallbacks.push(onError);
    }

    if (loading) {
        return;
    }
    loading = true;

    if (document.querySelector(SCRIPT_SELECTOR)) {
        startPolling();
        return;
    }

    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.dataset.hubspotFormsScript = '';
    script.addEventListener('error', () => flush(errorCallbacks, new Error('HubSpot embed script failed to load')));
    document.head.appendChild(script);

    // Poll from here rather than from the script's load event: a hung request, or one an
    // extension intercepts, fires neither load nor error and would leave `loading` true forever.
    startPolling();
};

export const createHubSpotForm = ({ portalId, formId, region, target }) => {
    window.hbspt.forms.create({
        portalId,
        formId,
        region: region || DEFAULT_REGION,
        target,
        // Without this HubSpot renders the form inside an iframe, which our page styles cannot reach.
        css: ''
    });
};

/**
 * Renders the HubSpot form described by a container's data attributes into its target element.
 *
 * Expected markup:
 *   <div data-hubspot-form data-portal-id="…" data-form-id="…" data-region="na1">
 *       <div data-hubspot-form-target></div>
 *   </div>
 */
export const renderHubSpotForm = (container, { targetSelector = '[data-hubspot-form-target]' } = {}) => {
    const target = container.querySelector(targetSelector);
    const { portalId, formId, region, errorMessage } = container.dataset;

    if (!target || !portalId || !formId) {
        return;
    }

    if (!target.id) {
        targetCounter += 1;
        target.id = `hubspot-form-target-${targetCounter}`;
    }

    container.setAttribute('aria-busy', 'true');

    whenHubSpotReady(
        () => {
            target.textContent = '';
            createHubSpotForm({ portalId, formId, region, target: `#${target.id}` });
            container.setAttribute('aria-busy', 'false');
        },
        () => {
            target.textContent = errorMessage || DEFAULT_ERROR_MESSAGE;
            container.setAttribute('aria-busy', 'false');
        }
    );
};

export const initHubSpotForms = (root = document) => {
    root.querySelectorAll('[data-hubspot-form]').forEach((container) => renderHubSpotForm(container));
};
